import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";

import Button, { ButtonProps } from "../../button/Button";
import useThemeService from "../../../services/theme-service/ThemeService";
import { Detection } from "../../svg/svgImageList";
import { translationService } from "../../../services/translation/TranslationService";
import useModal from "../../dialog/useModal";
import InformationBeforeAction from "../../data-components/InformationBeforeAction";
import WarningBeforeAction from "../../data-components/WarningBeforeAction";
import Checkbox from "../../checkbox/Checkbox";
import { api } from "../../../services/api/ApiService";
import { SubscriptionPlanUsageStorageModel } from "../../../services/organization-subscription-plans/SubscriptionPlanUsageStorage";
import { OrganizationSubscriptionPlansStorageModel } from "../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import { getLocalPrice } from "../../../helpers/MathHelpers";
import { RunVideoAnalysisServiceModel } from "../../../services/inspection/RunVideoAnalysisService";

export interface RunHighlightDetectionButtonProps extends ButtonProps {
  disabled?: boolean;
  confirmationAction: () => void;
  shouldDisplayModalForTwoCallsPerformed: boolean;
}

export default observer(function RunHighlightDetectionButton(
  props: RunHighlightDetectionButtonProps
) {
  const { themeClass } = useThemeService();
  const { toggleModal: toggleConfirmationModal, isVisible: confirmationModalVisible } = useModal();
  const { toggleModal: toggleExcedeCallsModal, isVisible: excedeCallsModalVisible } = useModal();
  const { toggleModal: toggleBothCallsModal, isVisible: bothCallsModalVisible } = useModal();
  const { disabled, confirmationAction, shouldDisplayModalForTwoCallsPerformed } = props;

  const [subscriptionPlanUsageStorage] = useState(() =>
    SubscriptionPlanUsageStorageModel.create({ autoLoad: false }, { api })
  );

  const [organizationSubscriptionPlansStorage] = useState(() =>
    OrganizationSubscriptionPlansStorageModel.create({ autoLoad: false }, { api })
  );

  const [runVideoAnalysisService] = useState(() => RunVideoAnalysisServiceModel.create({}, {}));

  const onClick = useCallback(async () => {
    const videoCallsUsage =
      await subscriptionPlanUsageStorage.getOrganizationVideoAnalysisCallsUsage();

    if (runVideoAnalysisService.VACallsStillAvailableAndPopupWasNotDismissed(videoCallsUsage)) {
      if (shouldDisplayModalForTwoCallsPerformed) toggleBothCallsModal();
      else toggleConfirmationModal();
      return;
    }

    if (
      runVideoAnalysisService.VACallsStillAvailableAndPopupWasPreviouslyDismissed(videoCallsUsage)
    ) {
      runVideoAnalysisService.clearVACallsPreferencesFromStorage();
      if (shouldDisplayModalForTwoCallsPerformed) toggleBothCallsModal();
      else toggleConfirmationModal();
      return;
    }

    if (runVideoAnalysisService.VACallsNotAvailableAndPopupWasNotDismissed(videoCallsUsage)) {
      await organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
      toggleExcedeCallsModal();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasDismissedInThisBillingCycle(
        videoCallsUsage
      )
    ) {
      if (shouldDisplayModalForTwoCallsPerformed) toggleBothCallsModal();
      else toggleConfirmationModal();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasDismissedInPreviousBillingCycle(
        videoCallsUsage
      )
    ) {
      runVideoAnalysisService.clearVACallsPreferencesFromStorage();
      await organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
      toggleExcedeCallsModal();
      return;
    }
  }, [
    subscriptionPlanUsageStorage,
    runVideoAnalysisService,
    shouldDisplayModalForTwoCallsPerformed,
    organizationSubscriptionPlansStorage,
    toggleBothCallsModal,
    toggleConfirmationModal,
    toggleExcedeCallsModal,
  ]);

  const handleConfirmationAction = () => {
    if (runVideoAnalysisService.isDoNotShowCallsExceededWarningMessageAgainSelected) {
      runVideoAnalysisService.disableVACallsExceededWarningMessageForOrganization();
    }

    if (shouldDisplayModalForTwoCallsPerformed) toggleBothCallsModal();
    if (!shouldDisplayModalForTwoCallsPerformed) confirmationAction();
    toggleExcedeCallsModal();
  };

  const handleRunBothClick = () => {
    confirmationAction();
    toggleBothCallsModal();
  };

  useEffect(() => {
    runVideoAnalysisService.setIsDoNotShowVACallsExceededWarningMessageAgainSelected(false);
  }, [excedeCallsModalVisible, runVideoAnalysisService]);

  return (
    <>
      <Button
        id="run-highlight-detection"
        onClick={onClick}
        disabled={disabled}
        icon={{
          svg: Detection,
          textKey: "RunHighlightDetectionButton.run",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-video"]),
        }}
      />
      <WarningBeforeAction
        isVisible={confirmationModalVisible}
        toggleModal={toggleConfirmationModal}
        titleKey="damages.RunHighlightDetection.confirmation-warning-title"
        buttonOk={{
          id: "run-va-confirmation",
          textKey: "RunHighlightDetectionButton.run",
          action: () => {
            confirmationAction();
            toggleConfirmationModal();
          },
        }}
      >
        {translationService.translate(
          "damages.RunHighlightDetection.confirmation-warning-subtitle"
        )}
      </WarningBeforeAction>
      <InformationBeforeAction
        isVisible={excedeCallsModalVisible}
        toggleModal={toggleExcedeCallsModal}
        titleKey="damages.RunVideoAnalysis.information-warning-title"
        buttonOk={{
          id: "run-va-ok",
          textKey: "general.ok",
          action: handleConfirmationAction,
        }}
      >
        <>
          {translationService.translate(
            "damages.RunVideoAnalysis.information-warning-subtitle-part-1"
          )}
          <Link to={"/subscription/plans"}>
            {translationService.translate(
              "damages.RunVideoAnalysis.information-warning-subtitle-part-2"
            )}
          </Link>
          {translationService.translate(
            "damages.RunVideoAnalysis.information-warning-subtitle-part-3",
            {
              price: getLocalPrice(
                organizationSubscriptionPlansStorage.extraVideoAnalysisUnitPrice
              ),
            }
          )}
        </>
        <Checkbox
          className="run-ai-warning-message-checkbox"
          checked={runVideoAnalysisService.isDoNotShowCallsExceededWarningMessageAgainSelected}
          onChange={runVideoAnalysisService.toggleIsDoNotShowThisPopupAgainSelected}
          labelKey="general.dont-show-this-message-again"
        />
      </InformationBeforeAction>
      <WarningBeforeAction
        isVisible={bothCallsModalVisible}
        toggleModal={toggleBothCallsModal}
        titleKey="VideoAnalysis.run-both-modal-title"
        buttonOk={{
          id: "run-both",
          textKey: "VideoAnalysis.run-both",
          action: handleRunBothClick,
        }}
      >
        <>{translationService.translate("VideoAnalysis.run-both-modal-body")}</>
      </WarningBeforeAction>
    </>
  );
});
